import { FC, ReactElement, useRef } from 'react';
import { API } from '@brg/api';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ProjectLongreadWrapper } from '../../styles';
import ResizedImageEnhanced from '../../../../components/ResizedImageEnhanced';
import { IContentViewItem } from '../../../../hooks/api/useContentView';
import { useContentViewTracking } from '../../../../hooks/api/useContentViewTracking';
import { LayoutConst } from 'core';
import { AppTester } from 'supporting';
import { useBreakpoint } from '@brg/ui';
import { useTheme } from 'styled-components';
import { getTooltipStyles } from 'shared/constants/tooptipStyles';
import { AdLegalHintTooltip } from './AdLegalHintTooltip';

const Wrapper = styled.div<{ bgColor: string }>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  width: 100%;
  box-sizing: border-box;
  column-gap: 24px;
  margin-bottom: 24px;
  background: ${({ bgColor }) => bgColor};
  
  @media (max-width: ${LayoutConst.screen.l}px) {
    margin-bottom: 16px;
    // max-width: 712px;
    // margin: 0 auto;
    column-gap: 16px;
  }

`;

// мобильная вёрстка, повторяющая StartingAdBlock
const MobileWrapper = styled.a`
  display: flex;
  flex-direction: column;
  width: calc(100% - 20px);
  margin: 0 10px;
  text-decoration: none;
  color: inherit;
  box-sizing: border-box;
  position: relative;
`;

const ImageWrapper = styled.div`
  height: auto;
`;

const Image = styled(ResizedImageEnhanced)`
  width: 100%;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    max-height: 100%;
  }
`;

const MobileImage = styled(ResizedImageEnhanced)`
  width: 100%;
  margin-bottom: 10px;
  overflow: hidden;
  max-height: 260px;

  img {
    width: 100%;
    object-fit: cover;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    max-height: 100%;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  padding: 24px 40px 24px 0;

  @media (max-width: ${LayoutConst.screen.l}px) {
    padding: 16px 24px 16px 0;
  }

  @media (max-width: ${LayoutConst.screen.s - 1}px) {
    padding: 0;
    min-height: auto;
  }
`;

const Badge = styled.span<{ $onDarkBg: boolean }>`
  font-size: 17px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: -0.34px;
  color: ${({ $onDarkBg }) => ($onDarkBg ? '#FFFFFF' : '#151514')};
  margin-bottom: 8px;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    font-size: 15px;
    line-height: 20px;
  }
  @media (max-width: ${LayoutConst.screen.m}px) {
    margin-bottom: 4px;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    background: rgba(49, 49, 49, 0.4);
    border-radius: 3px;
    padding: 6px 18px;
    font-size: 12px;
    line-height: 1.2;
    color: #cdcdcd;
    backdrop-filter: blur(4px);
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 2;
    margin-bottom: 0;
  }
`;

const Title = styled.div<{ $onDarkBg: boolean }>`
  font-size: 22px;
  font-weight: 600;
  line-height: 28px;
  letter-spacing: -0.44px;
  color: ${({ $onDarkBg }) => ($onDarkBg ? '#FFFFFF' : '#151514')};
  margin-bottom: 8px;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    font-size: 20px;
    line-height: 25px;
  }
  @media (max-width: ${LayoutConst.screen.m}px) {
    font-size: 17px;
    line-height: 22px;
    margin-bottom: 4px;
  }
`;

const SubTitle = styled.div<{ $onDarkBg: boolean }>`
  font-size: 13px;
  line-height: 17px;
  font-weight: 400;
  color: ${({ $onDarkBg }) => ($onDarkBg ? '#FFFFFF' : '#151514')};
  @media (max-width: ${LayoutConst.screen.mac13}px) {
    font-size: 15px;
    line-height: 20px;
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 11px;
    line-height: 13px;
    color: #909090;
  }
`;

const LinkRow = styled.div`
  display: inline-flex;
  align-items: center;
  column-gap: 6px;
  font-size: 17px;
  font-weight: 600;
  line-height: 22px;
  letter-spacing: -0.34px;
  color: #909090;

  position: absolute;
  bottom: 24px;
  @media (max-width: ${LayoutConst.screen.mac13}px) {
    font-size: 15px;
    line-height: 20px;
  }
  @media (max-width: ${LayoutConst.screen.m}px) {
    bottom: 16px;
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    display: none;
  }
`;

const LinkIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
    <path d="M1.21474 8L0 6.78603L5.12833 1.65587H1.27573L1.2859 0H8V6.71492H6.33291L6.34307 2.86984L1.21474 8Z" fill="#909090"/>
  </svg>
);

interface EndingAdBlockProps {
  adItem: IContentViewItem;
  projectId?: string;
  embedded?: boolean;
  position?: 0 | 1;
}

const EndingAdBlock: FC<EndingAdBlockProps> = ({ adItem, projectId, embedded, position = 1 }) => {
  const { i18n, t } = useTranslation();
  const theme = useTheme();
  const localized = API.lang<IContentViewItem>(adItem, i18n.language);


  const wrapperRef = useRef<HTMLAnchorElement>(null);
  const isMobile = useBreakpoint(LayoutConst.screen.s - 1);

  const { trackClick } = useContentViewTracking({
    adItem,
    projectId,
    position,
    elementRef: wrapperRef,
    componentName: 'EndingAdBlock',
  });

  const isMedia = AppTester.isMediiia();
  const isCGrave = AppTester.isCGRAVE();
  const isGeo = AppTester.isGeograffee();

  let bgColor = '#F2F2F2';

  if (isMedia) {
    bgColor = '#FFFFFF';
  } else if (isCGrave) {
    bgColor = '#2C2C2B';
  } else if (isGeo) {
    bgColor = '#3F4548';
  }

  const onDarkBg = isCGrave || isGeo;
  const tooltipStyles = getTooltipStyles(theme);

  const wrapWithLegalTooltip = (node: ReactElement) => (
    <AdLegalHintTooltip
      adItem={adItem}
      onWhiteBg={!onDarkBg}
      customStyle={tooltipStyles}
    >
      {node}
    </AdLegalHintTooltip>
  );

  if (isMobile) {
    return wrapWithLegalTooltip(
        <MobileWrapper
          ref={wrapperRef}
          href={adItem.link}
          target="_blank"
          rel="noopener noreferrer"
          onClick={trackClick}
          onAuxClick={trackClick}
          onContextMenu={trackClick}
        >
          {adItem.files?.length > 0 && (
            <ImageWrapper>
              <MobileImage files={adItem.files as any} alt={localized.title as any} />
            </ImageWrapper>
          )}
          <Badge $onDarkBg={onDarkBg}>{t('advertisement')}</Badge>
          {localized.title && <Title $onDarkBg={onDarkBg}>{localized.title as any}</Title>}
          {localized.subTitle && <SubTitle $onDarkBg={onDarkBg}>{localized.subTitle as any}</SubTitle>}
        </MobileWrapper>
    );
  }

  const adContent = wrapWithLegalTooltip(
    <Wrapper ref={wrapperRef} bgColor={bgColor}>
      {adItem.files?.length > 0 && (
        <ImageWrapper>
          <Image files={adItem.files as any} alt={localized.title as any} />
        </ImageWrapper>
      )}
      <Content>
        <Badge $onDarkBg={onDarkBg}>{t('advertisement')}</Badge>
        {localized.title && <Title $onDarkBg={onDarkBg}>{localized.title as any}</Title>}
        {localized.subTitle && <SubTitle $onDarkBg={onDarkBg}>{localized.subTitle as any}</SubTitle>}
        <a href={adItem.link} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }} onClick={trackClick} onAuxClick={trackClick} onContextMenu={trackClick}>
          <LinkRow>
            <span>Перейти</span>
            <LinkIcon />
          </LinkRow>
        </a>
      </Content>
    </Wrapper>
  );

  if (embedded) {
    return adContent;
  }

  return (
    <ProjectLongreadWrapper>
      {adContent}
    </ProjectLongreadWrapper>
  );
};

export default EndingAdBlock;

import { FC, ReactElement, useRef } from 'react';
import { API } from '@brg/api';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ProjectLongreadWrapper } from '../../styles';
import ResizedImageEnhanced from '../../../../components/ResizedImageEnhanced';
import { IContentViewItem } from '../../../../hooks/api/useContentView';
import { useContentViewTracking } from '../../../../hooks/api/useContentViewTracking';
import { LayoutConst } from 'core';
import { AppTester } from 'supporting';
import { useBreakpoint } from '@brg/ui';
import { useTheme } from 'styled-components';
import { getTooltipStyles } from 'shared/constants/tooptipStyles';
import { AdLegalHintTooltip } from './AdLegalHintTooltip';

const Wrapper = styled.div<{ bgColor: string }>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  width: 100%;
  box-sizing: border-box;
  column-gap: 24px;
  margin-bottom: 24px;
  background: ${({ bgColor }) => bgColor};
  
  @media (max-width: ${LayoutConst.screen.l}px) {
    margin-bottom: 16px;
    // max-width: 712px;
    // margin: 0 auto;
    column-gap: 16px;
  }

`;

// мобильная вёрстка, повторяющая StartingAdBlock
const MobileWrapper = styled.a`
  display: flex;
  flex-direction: column;
  width: calc(100% - 20px);
  margin: 0 10px;
  text-decoration: none;
  color: inherit;
  box-sizing: border-box;
  position: relative;
`;

const ImageWrapper = styled.div`
  height: auto;
`;

const Image = styled(ResizedImageEnhanced)`
  width: 100%;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    max-height: 100%;
  }
`;

const MobileImage = styled(ResizedImageEnhanced)`
  width: 100%;
  margin-bottom: 10px;
  overflow: hidden;
  max-height: 260px;

  img {
    width: 100%;
    object-fit: cover;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    max-height: 100%;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  padding: 24px 40px 24px 0;

  @media (max-width: ${LayoutConst.screen.l}px) {
    padding: 16px 24px 16px 0;
  }

  @media (max-width: ${LayoutConst.screen.s - 1}px) {
    padding: 0;
    min-height: auto;
  }
`;

const Badge = styled.span<{ $onDarkBg: boolean }>`
  font-size: 17px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: -0.34px;
  color: ${({ $onDarkBg }) => ($onDarkBg ? '#FFFFFF' : '#151514')};
  margin-bottom: 8px;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    font-size: 15px;
    line-height: 20px;
  }
  @media (max-width: ${LayoutConst.screen.m}px) {
    margin-bottom: 4px;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    background: rgba(49, 49, 49, 0.4);
    border-radius: 3px;
    padding: 6px 18px;
    font-size: 12px;
    line-height: 1.2;
    color: #cdcdcd;
    backdrop-filter: blur(4px);
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 2;
    margin-bottom: 0;
  }
`;

const Title = styled.div<{ $onDarkBg: boolean }>`
  font-size: 22px;
  font-weight: 600;
  line-height: 28px;
  letter-spacing: -0.44px;
  color: ${({ $onDarkBg }) => ($onDarkBg ? '#FFFFFF' : '#151514')};
  margin-bottom: 8px;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    font-size: 20px;
    line-height: 25px;
  }
  @media (max-width: ${LayoutConst.screen.m}px) {
    font-size: 17px;
    line-height: 22px;
    margin-bottom: 4px;
  }
`;

const SubTitle = styled.div<{ $onDarkBg: boolean }>`
  font-size: 13px;
  line-height: 17px;
  font-weight: 400;
  color: ${({ $onDarkBg }) => ($onDarkBg ? '#FFFFFF' : '#151514')};
  @media (max-width: ${LayoutConst.screen.mac13}px) {
    font-size: 15px;
    line-height: 20px;
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 11px;
    line-height: 13px;
    color: #909090;
  }
`;

const LinkRow = styled.div`
  display: inline-flex;
  align-items: center;
  column-gap: 6px;
  font-size: 17px;
  font-weight: 600;
  line-height: 22px;
  letter-spacing: -0.34px;
  color: #909090;

  position: absolute;
  bottom: 24px;
  @media (max-width: ${LayoutConst.screen.mac13}px) {
    font-size: 15px;
    line-height: 20px;
  }
  @media (max-width: ${LayoutConst.screen.m}px) {
    bottom: 16px;
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    display: none;
  }
`;

const LinkIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
    <path d="M1.21474 8L0 6.78603L5.12833 1.65587H1.27573L1.2859 0H8V6.71492H6.33291L6.34307 2.86984L1.21474 8Z" fill="#909090"/>
  </svg>
);

interface EndingAdBlockProps {
  adItem: IContentViewItem;
  projectId?: string;
  embedded?: boolean;
  position?: 0 | 1;
}

const EndingAdBlock: FC<EndingAdBlockProps> = ({ adItem, projectId, embedded, position = 1 }) => {
  const { i18n, t } = useTranslation();
  const theme = useTheme();
  const localized = API.lang<IContentViewItem>(adItem, i18n.language);


  const wrapperRef = useRef<HTMLAnchorElement>(null);
  const isMobile = useBreakpoint(LayoutConst.screen.s - 1);

  const { trackClick } = useContentViewTracking({
    adItem,
    projectId,
    position,
    elementRef: wrapperRef,
    componentName: 'EndingAdBlock',
  });

  const isMedia = AppTester.isMediiia();
  const isCGrave = AppTester.isCGRAVE();
  const isGeo = AppTester.isGeograffee();

  let bgColor = '#F2F2F2';

  if (isMedia) {
    bgColor = '#FFFFFF';
  } else if (isCGrave) {
    bgColor = '#2C2C2B';
  } else if (isGeo) {
    bgColor = '#3F4548';
  }

  const onDarkBg = isCGrave || isGeo;
  const tooltipStyles = getTooltipStyles(theme);

  const wrapWithLegalTooltip = (node: ReactElement) => (
    <AdLegalHintTooltip
      adItem={adItem}
      onWhiteBg={!onDarkBg}
      customStyle={tooltipStyles}
    >
      {node}
    </AdLegalHintTooltip>
  );

  if (isMobile) {
    return wrapWithLegalTooltip(
        <MobileWrapper
          ref={wrapperRef}
          href={adItem.link}
          target="_blank"
          rel="noopener noreferrer"
          onClick={trackClick}
          onAuxClick={trackClick}
          onContextMenu={trackClick}
        >
          {adItem.files?.length > 0 && (
            <ImageWrapper>
              <MobileImage files={adItem.files as any} alt={localized.title as any} />
            </ImageWrapper>
          )}
          <Badge $onDarkBg={onDarkBg}>{t('advertisement')}</Badge>
          {localized.title && <Title $onDarkBg={onDarkBg}>{localized.title as any}</Title>}
          {localized.subTitle && <SubTitle $onDarkBg={onDarkBg}>{localized.subTitle as any}</SubTitle>}
        </MobileWrapper>
    );
  }

  const adContent = wrapWithLegalTooltip(
    <Wrapper ref={wrapperRef} bgColor={bgColor}>
      {adItem.files?.length > 0 && (
        <ImageWrapper>
          <Image files={adItem.files as any} alt={localized.title as any} />
        </ImageWrapper>
      )}
      <Content>
        <Badge $onDarkBg={onDarkBg}>{t('advertisement')}</Badge>
        {localized.title && <Title $onDarkBg={onDarkBg}>{localized.title as any}</Title>}
        {localized.subTitle && <SubTitle $onDarkBg={onDarkBg}>{localized.subTitle as any}</SubTitle>}
        <a href={adItem.link} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }} onClick={trackClick} onAuxClick={trackClick} onContextMenu={trackClick}>
          <LinkRow>
            <span>Перейти</span>
            <LinkIcon />
          </LinkRow>
        </a>
      </Content>
    </Wrapper>
  );

  if (embedded) {
    return adContent;
  }

  return (
    <ProjectLongreadWrapper>
      {adContent}
    </ProjectLongreadWrapper>
  );
};

export default EndingAdBlock;

